/**
 * Theme bootstrap (docs/03-architecture.md §4.5, docs/05-ui-design.md §4, §6, §8). Applies
 * `data-palette` / `data-accent` / `data-glow` / `data-glass` / `data-motion` on `<html>` and
 * injects the theme stylesheet generated from `@finbob/shared` — the single source of truth also
 * used by the contrast tests, so the app can never drift from what's been verified accessible.
 *
 * A localStorage copy renders correctly on first paint, before `/me` has loaded; once the real
 * preferences arrive they take over (see features/auth/useSession.ts).
 */
import {
  type AccentColorId,
  ACCENT_COLOR_IDS,
  canvasSettingsSchema,
  type UserPreferences,
  DEFAULT_PREFERENCES,
  PALETTE_IDS,
  type PaletteId,
  CANVAS_PATTERNS,
  HOUSEHOLD_FONTS,
  type HouseholdFont,
  type CanvasPattern,
  themeStylesheet,
} from '@finbob/shared';

const STORAGE_KEY = 'finbob.theme';

export interface LocalThemePrefs {
  palette: PaletteId;
  accentColor: AccentColorId;
  customAccent?: string | null;
  canvasSettings?: UserPreferences['canvasSettings'];
  canvasPattern?: CanvasPattern;
  householdFont?: HouseholdFont;
  householdNameSize?: number;
  reducedGlow: boolean;
  glass: boolean;
  motion: 'system' | 'on' | 'off';
  theme: 'system' | 'dark' | 'light';
  privacyMode: boolean;
}

function defaults(): LocalThemePrefs {
  return {
    palette: DEFAULT_PREFERENCES.palette,
    customAccent: null,
    canvasSettings: DEFAULT_PREFERENCES.canvasSettings,
    accentColor: DEFAULT_PREFERENCES.accentColor,
    canvasPattern: DEFAULT_PREFERENCES.canvasPattern,
    householdFont: DEFAULT_PREFERENCES.householdFont,
    householdNameSize: DEFAULT_PREFERENCES.householdNameSize,
    reducedGlow: DEFAULT_PREFERENCES.reducedGlow,
    glass: DEFAULT_PREFERENCES.glass,
    motion: DEFAULT_PREFERENCES.motion,
    theme: DEFAULT_PREFERENCES.theme,
    privacyMode: DEFAULT_PREFERENCES.privacyMode,
  };
}

function isPaletteId(value: unknown): value is PaletteId {
  return typeof value === 'string' && (PALETTE_IDS as readonly string[]).includes(value);
}

/** Never throws — private browsing / disabled storage just means no persistence, not a crash. */
export function readLocalThemePrefs(): LocalThemePrefs {
  const base = defaults();
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return base;
    const parsed = JSON.parse(raw) as Partial<LocalThemePrefs>;
    return {
      palette: isPaletteId(parsed.palette) ? parsed.palette : base.palette,
      accentColor:
        parsed.accentColor && ACCENT_COLOR_IDS.includes(parsed.accentColor)
          ? parsed.accentColor
          : base.accentColor,
      customAccent:
        typeof parsed.customAccent === 'string' && /^#[0-9a-fA-F]{6}$/.test(parsed.customAccent)
          ? parsed.customAccent
          : null,
      canvasSettings: canvasSettingsSchema.safeParse(parsed.canvasSettings).success
        ? parsed.canvasSettings
        : base.canvasSettings,
      householdNameSize:
        typeof parsed.householdNameSize === 'number' &&
        Number.isInteger(parsed.householdNameSize) &&
        parsed.householdNameSize >= 14 &&
        parsed.householdNameSize <= 28
          ? parsed.householdNameSize
          : base.householdNameSize,
      householdFont: HOUSEHOLD_FONTS.includes(parsed.householdFont as HouseholdFont)
        ? parsed.householdFont
        : base.householdFont,
      canvasPattern: CANVAS_PATTERNS.includes(parsed.canvasPattern as CanvasPattern)
        ? parsed.canvasPattern
        : base.canvasPattern,
      reducedGlow: typeof parsed.reducedGlow === 'boolean' ? parsed.reducedGlow : base.reducedGlow,
      glass: typeof parsed.glass === 'boolean' ? parsed.glass : base.glass,
      motion: parsed.motion === 'on' || parsed.motion === 'off' ? parsed.motion : base.motion,
      theme: parsed.theme === 'dark' || parsed.theme === 'light' ? parsed.theme : base.theme,
      privacyMode: typeof parsed.privacyMode === 'boolean' ? parsed.privacyMode : base.privacyMode,
    };
  } catch {
    return base;
  }
}

export function writeLocalThemePrefs(prefs: LocalThemePrefs): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(prefs));
  } catch {
    // ignore — theming still works for this tab, it just won't persist across reloads
  }
}

let stylesheetInjected = false;

/** Injects every palette + accent preset once (all gated behind attribute selectors, cheap to keep all of them). */
export function ensureThemeStylesheet(): void {
  if (stylesheetInjected || typeof document === 'undefined') return;
  const style = document.createElement('style');
  style.id = 'finbob-theme';
  style.textContent = themeStylesheet();
  document.head.appendChild(style);
  stylesheetInjected = true;
}

/** Sets attributes only for non-default states, matching the CSS rules in styles/*.css. */
export function applyThemeAttributes(prefs: LocalThemePrefs): void {
  if (typeof document === 'undefined') return;
  ensureThemeStylesheet();
  const root = document.documentElement;
  root.setAttribute('data-palette', prefs.palette);
  root.setAttribute('data-accent', prefs.accentColor);
  for (const property of ['--accent', '--accent-rgb', '--fill-text'])
    root.style.removeProperty(property);
  const customAccent = prefs.customAccent;
  if (customAccent && /^#[0-9a-fA-F]{6}$/.test(customAccent)) {
    const rgb = [1, 3, 5].map((start) => parseInt(customAccent.slice(start, start + 2), 16));
    const luminance = rgb.reduce((sum, channel, index) => {
      const value = channel / 255;
      return (
        sum +
        (value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4) *
          ([0.2126, 0.7152, 0.0722][index] ?? 0)
      );
    }, 0);
    root.style.setProperty('--accent', customAccent);
    root.style.setProperty('--accent-rgb', rgb.join(' '));
    root.style.setProperty('--fill-text', luminance > 0.179 ? '#000000' : '#ffffff');
  }
  root.setAttribute('data-canvas', prefs.canvasPattern ?? 'linen');
  if (prefs.reducedGlow) root.setAttribute('data-glow', 'off');
  else root.removeAttribute('data-glow');
  if (!prefs.glass) root.setAttribute('data-glass', 'off');
  else root.removeAttribute('data-glass');
  if (prefs.privacyMode) root.setAttribute('data-privacy', 'on');
  else root.removeAttribute('data-privacy');
  if (prefs.motion === 'system') root.removeAttribute('data-motion');
  else root.setAttribute('data-motion', prefs.motion);
  if (prefs.theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', prefs.theme);

  // <meta name="theme-color"> (the mobile browser/PWA status-bar tint) has no CSS equivalent —
  // unlike color-scheme in tokens.css, it has to be set from here. 'system' resolves once from
  // the OS media query at the moment preferences change/apply; it won't re-run if the OS scheme
  // flips while the tab stays open and idle with no other preference change — an accepted, minor
  // gap for a status-bar colour, not page content.
  const resolvedLight =
    prefs.theme === 'light' ||
    (prefs.theme === 'system' &&
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-color-scheme: light)').matches);
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', resolvedLight ? '#ffffff' : '#000000');
}
